import { useRef } from 'react';

import EllipsisText from '@webclient/components/ellipsis-typography';
import LIST_CONFIG from '@webclient/components/pages/home/sections/bots/list/config';
import {
  StyledButton,
  StyledFormControlLabel,
  StyledHiddenPositionedRadio,
  StyledIconBox,
  StyledImageListItem,
  StyledImg,
  StyledListItemBar,
  StyledNameStack,
  StyledVideo,
} from '@webclient/components/pages/home/sections/bots/list/styles';

import CONFIG from '@webclient/lib/const/bots';
import createPublicImageUrl, {
  retrieveImageWithFallback,
} from '@webclient/lib/createPublicImageUrl';

import useAppSelector from '@webclient/hooks/useAppSelector';

import { selectBot } from '@webclient/state/slices/app-settings/userSettingSelector';

import { BotsResponseType } from '@webclient/shared-types/bots';

import VolumeMaxRoundedIcon from '~icons/knowz-iconify/volume-max-rounded';

import { Text } from '@knowz/knowz-ui';
import { useTranslate } from '@tolgee/react';

type Props = BotsResponseType[number] & {
  getAudioUrl: (url: string | null) => void;
};

const CHARACTER_FALLBACK =
  'https://asset.knowz.com/assets/images/not-found-characters-%5Boptimised%5D.webp';

export default function ListItem({
  id,
  category,
  image,
  name,
  animationVideo,
  introAudio,
  description,
  getAudioUrl,
  isDefaultOwner,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const bot = useAppSelector(selectBot);

  const { t } = useTranslate();

  function handleClickPlay(
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    getAudioUrl(introAudio);
  }

  const isItKnowzAI = id === CONFIG.DEFAULT_KNOWZ_AI_BOT.id;

  return (
    <StyledImageListItem selected={id === bot.id}>
      <StyledFormControlLabel
        control={<StyledHiddenPositionedRadio />}
        value={id}
        label={
          <StyledImageListItem
            component="span"
            title={name}
          >
            <StyledNameStack gap={1}>
              <StyledIconBox>
                {CONFIG.CATEGORY_BY_ICONS[category]}
              </StyledIconBox>
              <Text
                variant="textXs"
                weight="semibold"
                color="text.primary"
                sx={{
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {name}
              </Text>
            </StyledNameStack>
            {id === bot.id && isDefaultOwner ? (
              <StyledVideo
                poster={createPublicImageUrl(image)}
                ref={videoRef}
                playsInline
                src={
                  isItKnowzAI && animationVideo
                    ? animationVideo
                    : createPublicImageUrl(animationVideo)
                }
                autoPlay
                width={LIST_CONFIG.IMAGE_WIDHT}
                height={LIST_CONFIG.IMAGE_HEIGHT}
                controls={false}
                draggable={false}
              />
            ) : (
              <StyledImg
                draggable={false}
                width={LIST_CONFIG.IMAGE_WIDHT}
                height={LIST_CONFIG.IMAGE_HEIGHT}
                loading="lazy"
                src={retrieveImageWithFallback(
                  createPublicImageUrl(image),
                  CHARACTER_FALLBACK,
                )}
                alt={name}
              />
            )}
            {id === bot.id && isDefaultOwner && (
              <audio src={createPublicImageUrl(introAudio)} />
            )}
            <StyledListItemBar
              title={
                !isItKnowzAI && isDefaultOwner ? (
                  <StyledButton onClick={handleClickPlay}>
                    <VolumeMaxRoundedIcon />
                  </StyledButton>
                ) : null
              }
              subtitle={
                <span
                  role="button"
                  onClick={handleClickPlay}
                >
                  <EllipsisText
                    title={description}
                    variant="textXs"
                    weight="medium"
                    lineClamp={2}
                  >
                    {isItKnowzAI
                      ? t(
                          CONFIG.DEFAULT_KNOWZ_AI_BOT.descriptionTranslationKey,
                          description,
                        )
                      : description}
                  </EllipsisText>
                </span>
              }
            />
          </StyledImageListItem>
        }
      />
    </StyledImageListItem>
  );
}
